import { createContext, useContext, useState, type ReactNode } from 'react';
import type { WorkbookDefinition } from '@erp/shared-types';
import {
  processOutline,
  type ExecutionRoot,
  type ProcessOutline,
} from '@erp/workbooks';
import type { WorkbookEdits } from './workbookEdits';
import {
  useWorkbookReferences,
  type WorkbookReferenceLabels,
} from './useWorkbookReferences';
import { useWorkbookGeneration } from './useWorkbookGeneration';

/** What every part of the Process panel reads: one Outline, one resolver, one editor. */
export interface WorkbookProcessState {
  definition: WorkbookDefinition;
  outline: ProcessOutline;
  references: WorkbookReferenceLabels;
  edits: WorkbookEdits;
  canUpdate: boolean;
  generation: ReturnType<typeof useWorkbookGeneration>;
  /** The one Instruction whose editor is open. */
  editingId: string | null;
  setEditingId: (id: string | null) => void;
  openItem: (id: string) => void;
  /** Inserts an empty Instruction, linked to `itemIds` when given, and opens its editor. */
  insert: (
    parentId: string | null,
    afterId?: string,
    itemIds?: readonly string[],
  ) => void;
  /** The Run modes shown on a root's cell, if any. */
  renderRunMenu?: (root: ExecutionRoot) => ReactNode;
  /** What the AI check says about an Instruction up for review, where one runs. */
  reviewCheck?: (stepId: string) => ReactNode;
  /** The integrations' actions on an Instruction (the `process-step` surface), where the page offers them. */
  renderStepActions?: (stepId: string) => ReactNode;
}

export const WorkbookProcessContext =
  createContext<WorkbookProcessState | null>(null);

/**
 * The Process state for one surface that edits Instructions: the Process tab,
 * or an Item's own Instructions in its detail tab. Each keeps its own open
 * editor and Generation.
 */
export function useWorkbookProcessState({
  definition,
  edits,
  canUpdate,
  workbookId,
  openItem,
  renderRunMenu,
  reviewCheck,
  renderStepActions,
}: Pick<
  WorkbookProcessState,
  | 'definition'
  | 'edits'
  | 'canUpdate'
  | 'openItem'
  | 'renderRunMenu'
  | 'reviewCheck'
  | 'renderStepActions'
> & {
  workbookId: string;
}): WorkbookProcessState {
  const references = useWorkbookReferences(definition, openItem);
  const [editingId, setEditingId] = useState<string | null>(null);
  const generation = useWorkbookGeneration(workbookId, definition);
  const outline = processOutline(definition);
  return {
    definition,
    outline,
    references,
    edits,
    canUpdate,
    generation,
    editingId,
    setEditingId,
    openItem,
    insert: (parentId, afterId, itemIds) =>
      setEditingId(edits.insertStep(parentId, afterId, itemIds)),
    renderRunMenu,
    reviewCheck,
    renderStepActions,
  };
}

export function useWorkbookProcess(): WorkbookProcessState {
  const state = useContext(WorkbookProcessContext);
  if (!state)
    throw new Error('useWorkbookProcess must be used inside WorkbookProcess.');
  return state;
}
